<template>
  <div class="whatsapp-not-connected">
    <v-row align="stretch">
      <v-col cols="12" lg="8">
        <v-card rounded="lg" elevation="0" class="pa-6 h-100">
          <div class="d-flex align-start mb-6">
            <v-sheet
              color="green-lighten-4"
              rounded="lg"
              class="d-flex align-center justify-center flex-shrink-0 mr-4"
              width="56"
              height="56"
            >
              <v-icon color="green-darken-2" size="32">
                mdi-whatsapp
              </v-icon>
            </v-sheet>
            <div>
              <h2 class="text-h5 font-weight-bold mb-2">
                {{ $t('settings.whatsapp.notConnected.heroTitle') }}
              </h2>
              <p class="text-body-1 text-medium-emphasis mb-0">
                {{ $t('settings.whatsapp.notConnected.heroSubtitle') }}
              </p>
            </div>
          </div>

          <v-row class="mb-6">
            <v-col
              v-for="(feature, idx) in features"
              :key="idx"
              cols="12"
              md="4"
            >
              <v-sheet
                color="grey-lighten-4"
                rounded="lg"
                class="pa-4 h-100"
              >
                <v-icon :color="feature.color" class="mb-2">
                  {{ feature.icon }}
                </v-icon>
                <p class="text-subtitle-2 font-weight-bold mb-1">
                  {{ $t(feature.titleKey) }}
                </p>
                <p class="text-caption text-medium-emphasis mb-0">
                  {{ $t(feature.bodyKey) }}
                </p>
              </v-sheet>
            </v-col>
          </v-row>

          <v-alert
            v-if="!metaConfigured"
            type="warning"
            variant="tonal"
            density="comfortable"
            class="mb-4"
          >
            {{ metaMissingMessage }}
          </v-alert>

          <p
            v-if="isStaff && institutionName"
            class="text-body-2 font-weight-medium mb-3"
          >
            {{ $t('settings.whatsapp.notConnected.connectingFor', { name: institutionName }) }}
          </p>

          <p class="text-subtitle-2 font-weight-bold mb-2">
            {{ $t('settings.whatsapp.notConnected.connectModeTitle') }}
          </p>
          <v-radio-group
            v-model="connectionMode"
            color="red-darken-3"
            density="comfortable"
            class="mb-4"
            :disabled="connectLoading"
          >
            <v-radio
              value="coexistence"
              :label="$t('settings.whatsapp.notConnected.connectModeExisting')"
            />
            <p class="text-caption text-medium-emphasis ml-10 mt-n2 mb-2">
              {{ $t('settings.whatsapp.notConnected.connectModeExistingHint') }}
            </p>
            <v-radio
              value="dedicated"
              :label="$t('settings.whatsapp.notConnected.connectModeNew')"
            />
            <p class="text-caption text-medium-emphasis ml-10 mt-n2 mb-0">
              {{ $t('settings.whatsapp.notConnected.connectModeNewHint') }}
            </p>
          </v-radio-group>

          <v-btn
            color="red-darken-3"
            size="large"
            variant="flat"
            :disabled="!metaConfigured || !institutionId || connectLoading"
            :loading="connectLoading"
            @click="onConnectClick"
          >
            {{ $t('settings.whatsapp.notConnected.connectButton') }}
          </v-btn>
          <p class="text-caption text-medium-emphasis mt-3 mb-0">
            {{ $t('settings.whatsapp.notConnected.connectHint') }}
          </p>
        </v-card>
      </v-col>

      <v-col cols="12" lg="4">
        <v-card rounded="lg" elevation="0" class="pa-6 h-100">
          <h3 class="text-h6 font-weight-bold mb-4">
            {{ $t('settings.whatsapp.notConnected.beforeTitle') }}
          </h3>
          <ul class="whatsapp-prep-list pl-0 mb-0">
            <li
              v-for="(item, idx) in checklistKeys"
              :key="idx"
              class="d-flex align-start mb-3 text-body-2"
            >
              <span class="prep-bullet mr-3" aria-hidden="true" />
              <span>{{ $t(item) }}</span>
            </li>
          </ul>
          <v-divider class="my-4" />
          <p class="text-caption text-medium-emphasis mb-0">
            {{ $t('settings.whatsapp.notConnected.costNote') }}
          </p>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { connectWhatsapp } from '@/api/whatsapp';
import { whatsappErrorMessage } from '@/utils/whatsappErrors';
import {
  loadFacebookSdk,
  isEmbeddedSignupConfigured,
  buildEmbeddedSignupLoginOptions,
  logEmbeddedSignupMessage,
  EMBEDDED_SIGNUP_FINISH_EVENTS,
} from '@/utils/metaEmbeddedSignup';

const SESSION_WAIT_MS = 500;

export default {
  name: 'WhatsappNotConnected',
  props: {
    institutionId: {
      type: String,
      required: true,
    },
    institutionName: {
      type: String,
      default: '',
    },
    isStaff: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['connected', 'snackbar'],
  data() {
    return {
      connectLoading: false,
      /** @type {'coexistence'|'dedicated'} */
      connectionMode: 'coexistence',
      latestSession: null,
      signupAssets: null,
      messageHandler: null,
      features: [
        {
          icon: 'mdi-robot-outline',
          color: 'red-darken-3',
          titleKey: 'settings.whatsapp.notConnected.featureAssistantTitle',
          bodyKey: 'settings.whatsapp.notConnected.featureAssistantBody',
        },
        {
          icon: 'mdi-account-switch-outline',
          color: 'blue-darken-2',
          titleKey: 'settings.whatsapp.notConnected.featureStaffTitle',
          bodyKey: 'settings.whatsapp.notConnected.featureStaffBody',
        },
        {
          icon: 'mdi-lock-outline',
          color: 'grey-darken-2',
          titleKey: 'settings.whatsapp.notConnected.featurePrivateTitle',
          bodyKey: 'settings.whatsapp.notConnected.featurePrivateBody',
        },
      ],
      checklistKeys: [
        'settings.whatsapp.notConnected.check1',
        'settings.whatsapp.notConnected.check2',
        'settings.whatsapp.notConnected.check3',
        'settings.whatsapp.notConnected.check4',
        'settings.whatsapp.notConnected.check5',
      ],
    };
  },
  computed: {
    metaConfigured() {
      return isEmbeddedSignupConfigured();
    },
    metaMissingMessage() {
      if (this.isStaff) {
        return this.$t('settings.whatsapp.notConnected.metaMissing');
      }
      return this.$t('settings.whatsapp.notConnected.metaMissingContact');
    },
  },
  mounted() {
    this.messageHandler = (event) => this.onEmbeddedSignupMessage(event);
    window.addEventListener('message', this.messageHandler);
    if (this.metaConfigured) {
      loadFacebookSdk().catch(() => {});
    }
  },
  beforeUnmount() {
    if (this.messageHandler) {
      window.removeEventListener('message', this.messageHandler);
      this.messageHandler = null;
    }
  },
  methods: {
    onEmbeddedSignupMessage(event) {
      if (!event.origin || !event.origin.endsWith('facebook.com')) {
        return;
      }
      let data;
      try {
        data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
      } catch (e) {
        return;
      }
      if (!data || data.type !== 'WA_EMBEDDED_SIGNUP') {
        return;
      }
      logEmbeddedSignupMessage(data);
      if (data.event === 'ERROR') {
        this.latestSession = data;
        return;
      }
      if (EMBEDDED_SIGNUP_FINISH_EVENTS.has(data.event)) {
        this.latestSession = data;
        this.signupAssets = data.data ? { ...data.data } : null;
      }
    },
    onConnectClick() {
      if (this.connectLoading || !this.metaConfigured || !this.institutionId) {
        return;
      }
      this.connectLoading = true;
      this.latestSession = null;
      this.signupAssets = null;
      const self = this;
      const loginOptions = buildEmbeddedSignupLoginOptions(self.connectionMode);
      loadFacebookSdk()
        .then((FB) => {
          FB.login(
            (response) => {
              self.handleLoginResponse(response);
            },
            loginOptions,
          );
        })
        .catch((err) => {
          self.connectLoading = false;
          self.$emit('snackbar', {
            message: err.message || self.$t('settings.whatsapp.notConnected.sdkLoadFailed'),
            color: 'error',
          });
        });
    },
    handleLoginResponse(response) {
      const self = this;
      (async () => {
        try {
          const code = response && response.authResponse && response.authResponse.code;
          if (!code) {
            const step = self.latestSession
              && self.latestSession.data
              && self.latestSession.data.current_step;
            let message = self.$t('settings.whatsapp.notConnected.cancelled');
            if (step) {
              message += ` (${step})`;
            }
            self.$emit('snackbar', { message, color: 'info' });
            return;
          }

          await new Promise((r) => setTimeout(r, SESSION_WAIT_MS));

          if (self.latestSession && self.latestSession.event === 'ERROR') {
            const errData = self.latestSession.data || {};
            const errCode = errData.error_code || errData.error_message || 'ERROR';
            const sessionId = errData.session_id || errData.sessionId || '—';
            self.$emit('snackbar', {
              message: self.$t('settings.whatsapp.notConnected.embeddedError', {
                code: errCode,
                sessionId,
              }),
              color: 'error',
            });
            return;
          }

          const sessionPayload = self.latestSession || {};
          const rawData = sessionPayload.data || {};
          const assets = self.signupAssets ? { ...self.signupAssets } : { ...rawData };
          const res = await connectWhatsapp(self.institutionId, {
            mode: self.connectionMode,
            code,
            waba_id: assets.waba_id || assets.wabaId,
            phone_number_id: assets.phone_number_id || assets.phoneNumberId,
            business_id: assets.business_id || assets.businessId,
            session: sessionPayload,
          });

          const notRequestedReason = res.data && res.data.sync_not_requested_reason;
          if (res.status === 202 && notRequestedReason) {
            self.$emit('snackbar', {
              message: self.$t('settings.whatsapp.syncing.syncNotRequested', { reason: notRequestedReason }),
              color: 'warning',
            });
          } else if (res.status === 202) {
            self.$emit('snackbar', {
              message: self.$t('settings.whatsapp.notConnected.botPending'),
              color: 'warning',
            });
          } else {
            self.$emit('snackbar', {
              message: self.$t('settings.whatsapp.notConnected.connectSuccess'),
              color: 'success',
            });
          }
          self.$emit('connected');
        } catch (err) {
          self.$emit('snackbar', {
            message: whatsappErrorMessage(err),
            color: 'error',
          });
        } finally {
          self.connectLoading = false;
        }
      })();
    },
  },
};
</script>

<style scoped>
.whatsapp-prep-list {
  list-style: none;
}
.prep-bullet {
  flex-shrink: 0;
  width: 10px;
  height: 10px;
  margin-top: 6px;
  border: 2px solid rgba(0, 0, 0, 0.38);
  border-radius: 50%;
  background: transparent;
}
</style>
